import React from 'react'
import {
  Renderer,
  createLibrary,
  BuiltinActionType,
  type ActionEvent,
  type DefinedComponent,
} from '@openuidev/react-lang'
// Import from the package root (not the `/genui-lib` subpath) so the build's
// classic `moduleResolution: node` can resolve it — `openuiLibrary` is
// re-exported from the root entry.
import { ThemeProvider, openuiLibrary } from '@openuidev/react-ui'
import '@openuidev/react-ui/index.css'
import {
  useLabraChatContext,
  useLabraChatDispatch,
} from '../../../modules/labraChat/context'
import { sendLabraChatPrompt } from '../../../modules/labraChat/actions'
import { useStyles } from './OpenUiMessage.styles'

/** Marks the machine-readable half of a submission, mirroring the
 * `[Funding user uploads]` block the backend already parses out of a message. */
const SUBMISSION_MARKER = '[Form submission]'

/**
 * Renders an assistant message's generative UI. The agent's `render_ui` tool
 * returns OpenUI-Lang (see `extractRenderUiLang` in LabraChat actions); this
 * draws it through OpenUI's `<Renderer>` and the bundled `openuiLibrary`, so the
 * component vocabulary the backend emits (`Stack`, `Card`, `Table`, `BarChart`,
 * `TextContent`, …) maps to real react-ui components. `isStreaming` disables form
 * interaction and lets the tree resolve progressively as the program streams in.
 */
interface OpenUiMessageProps {
  response: string
  isStreaming?: boolean
}

export const OpenUiMessage: React.FC<OpenUiMessageProps> = ({
  response,
  isStreaming,
}) => {
  const { classes } = useStyles()
  const { openUiComponents, partnerId } = useLabraChatContext()
  const dispatch = useLabraChatDispatch()

  const handleAction = (event: ActionEvent) => {
    if (event.type === BuiltinActionType.OpenUrl) {
      const url = String(event.params?.url ?? '')
      if (url) window.open(url, '_blank', 'noopener,noreferrer')
      return
    }

    const payload = {
      action: event.type,
      form: event.formName,
      params: event.params,
      values: event.formState ?? {},
    }

    dispatch(
      sendLabraChatPrompt(
        partnerId,
        `${event.humanFriendlyMessage}\n\n${SUBMISSION_MARKER}\n${JSON.stringify(payload)}`,
        { displayMessage: event.humanFriendlyMessage }
      )
    )
  }

  // Merge any product-injected components into the base library once. The base
  // library ships the generic vocabulary; a product adds its own components
  // (e.g. a funding health card) via the provider's `openUiComponents`.
  const library = React.useMemo(() => {
    if (!openUiComponents.length) return openuiLibrary
    return createLibrary({
      components: [
        ...(Object.values(openuiLibrary.components) as DefinedComponent<any>[]),
        ...openUiComponents,
      ],
    })
  }, [openUiComponents])

  return (
    <div className={classes.root}>
      <ThemeProvider mode="light">
        <Renderer
          response={response}
          library={library}
          isStreaming={isStreaming}
          onAction={handleAction}
        />
      </ThemeProvider>
    </div>
  )
}
